import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import { logEvent } from "@/hooks/use-event-log";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { EXTRA_PAYMENT_EVENTS, type PaymentState } from "@/lib/extras-payment";

/** Én rad i DL-ens utbetalingsliste. */
export interface ExtraAwaitingPayment {
  id: string;
  project_id: string | null;
  full_name: string;
  role: string | null;
  agreed_amount: number;
  payment_type: string | null;
  personal_number: string | null;
  bank_account: string | null;
  ready_for_payment_at: string | null;
  project_code: string | null;
  project_name: string;
  /** Prosjektleder, så DL vet hvem som meldte klar. */
  production_lead_name: string | null;
}

interface RawAwaitingRow {
  id: string;
  project_id: string | null;
  full_name: string | null;
  name: string | null;
  role: string | null;
  agreed_amount: number | null;
  payment_type: string | null;
  personal_number: string | null;
  bank_account: string | null;
  ready_for_payment_at: string | null;
  projects: {
    name: string | null;
    project_code: string | null;
    production_lead: { full_name: string | null } | null;
  } | null;
}

const AWAITING_COLS =
  "id, project_id, full_name, name, role, agreed_amount, payment_type, personal_number, bank_account, ready_for_payment_at, projects:project_id(name, project_code, production_lead:production_lead_id(full_name))";

/**
 * VIB-2092: statister DL skal betale — på tvers av alle prosjekter.
 *
 * Team-bred lesing av `project_extras` uten `.eq('project_id', …)`, derfor
 * `fetchAllPaginated`: Supabase kutter rå select stille ved 1000 rader, og en
 * utbetalingsliste som mangler de siste radene er verre enn ingen liste.
 *
 * `enabled` styres av kallstedet (admin-sjekk + flagg), så vi ikke spør for
 * brukere som uansett ikke skal se lista.
 */
export function useExtrasAwaitingPayment(enabled: boolean) {
  return useQuery({
    queryKey: ["extras-awaiting-payment"],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ExtraAwaitingPayment[]> => {
      const rows = await fetchAllPaginated<RawAwaitingRow>(
        "project_extras",
        AWAITING_COLS,
        (q) =>
          q
            .eq("payment_state", "ready_for_payment")
            .is("deleted_at", null)
            .order("ready_for_payment_at", { ascending: true }),
      );

      return rows.map((r) => ({
        id: r.id,
        project_id: r.project_id,
        full_name: r.full_name ?? r.name ?? "(uten navn)",
        role: r.role,
        agreed_amount: Math.round(r.agreed_amount ?? 0),
        payment_type: r.payment_type,
        personal_number: r.personal_number,
        bank_account: r.bank_account,
        ready_for_payment_at: r.ready_for_payment_at,
        project_code: r.projects?.project_code ?? null,
        project_name: r.projects?.name ?? "(ukjent prosjekt)",
        production_lead_name: r.projects?.production_lead?.full_name ?? null,
      }));
    },
  });
}

interface SetPaymentStateInput {
  /** Radene som skal endres. Én ID er bare en batch på én. */
  extraIds: string[];
  state: PaymentState;
  /** For målrettet cache-invalidering når alle radene er på samme prosjekt. */
  projectId?: string;
  /** Navn til audit-loggen, så event_log er lesbart uten oppslag. */
  namesById?: Record<string, string>;
}

/**
 * Setter utbetalingsstatus på én eller flere statister.
 *
 * Aktøren er `useAuth().user`, ALDRI den impersonerte brukeren: dette er en
 * økonomi-handling, og CLAUDE.md audit-regel 1 krever at loggen bærer den
 * faktisk innloggede personen. Aktøren skrives både på raden
 * (`ready_for_payment_by` / `paid_by`) og i `event_log`.
 *
 * Merk at UPDATE-en bevisst rører KUN utbetalingsfeltene. Prosjekt-skrivelåsen
 * (`assert_project_writable`) slipper gjennom nettopp den formen på lukkede
 * prosjekter, og statister betales typisk etter at produksjonen er lukket. Tar
 * du med et annet felt her, blir skrivingen avvist på ferdigstilte prosjekter.
 */
export function useSetExtrasPaymentState() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: SetPaymentStateInput) => {
      if (input.extraIds.length === 0) return { count: 0 };
      const now = new Date().toISOString();

      const patch: Record<string, unknown> = { payment_state: input.state };
      if (input.state === "ready_for_payment") {
        patch.ready_for_payment_at = now;
        patch.ready_for_payment_by = user?.id ?? null;
      } else if (input.state === "paid") {
        patch.paid_at = now;
        patch.paid_by = user?.id ?? null;
      } else {
        // Tilbake til utkast: nullstill begge sporene, ellers ser raden ut som
        // den er meldt klar av noen selv om den ikke er det lenger.
        patch.ready_for_payment_at = null;
        patch.ready_for_payment_by = null;
        patch.paid_at = null;
        patch.paid_by = null;
      }

      const { error } = await supabase
        .from("project_extras")
        .update(patch)
        .in("id", input.extraIds);
      if (error) throw error;

      return { count: input.extraIds.length };
    },
    onSuccess: async (result, vars) => {
      await invalidateFor(queryClient, "project_extras", { projectId: vars.projectId });

      // Én loggpost per rad: en samlepost ville gjort det umulig å se i
      // ProjectLogTab hvem som ble meldt klar når.
      if (vars.state !== "draft") {
        const eventType =
          vars.state === "ready_for_payment"
            ? EXTRA_PAYMENT_EVENTS.ready_for_payment
            : EXTRA_PAYMENT_EVENTS.paid;
        for (const id of vars.extraIds) {
          logEvent({
            event_type: eventType,
            entity_type: "project_extra",
            entity_id: id,
            project_id: vars.projectId,
            user_id: user?.id,
            payload: { extra_name: vars.namesById?.[id] ?? null, state: vars.state },
          });
        }
      }

      const n = result.count;
      if (vars.state === "ready_for_payment") {
        toast.success(
          n === 1 ? "Meldt klar for utbetaling" : `${n} meldt klare for utbetaling`,
        );
      } else if (vars.state === "paid") {
        toast.success(n === 1 ? "Markert som utbetalt" : `${n} markert som utbetalt`);
      } else {
        toast.success(n === 1 ? "Satt tilbake til utkast" : `${n} satt tilbake til utkast`);
      }
    },
    onError: (err: Error) => {
      // Skrivelåsen svarer med en egen, lesbar melding — vis den framfor å
      // erstatte den med noe generisk.
      toast.error(err?.message ?? "Kunne ikke endre utbetalingsstatus");
    },
  });
}
